import { API_BASE_URL } from './apiConfig';
import { unregisterCurrentDevice } from './fcmService';

/**
 * Login user and store JWT token
 */
export async function loginUser(email, password, expectedRole) {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, role: expectedRole })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Login failed');
  }

  const data = await response.json();

  // Validate role matches if provided
  if (expectedRole && data.user.role !== expectedRole) {
    throw new Error(`Access denied. This page is for ${expectedRole} users only.`);
  }

  // Store token and user info
  localStorage.setItem('authToken', data.token);
  localStorage.setItem('authUser', JSON.stringify(data.user));

  return data;
}

/**
 * Get stored auth token
 */
export function getAuthToken() {
  return localStorage.getItem('authToken');
}

/**
 * Get stored user info
 */
export function getAuthUser() {
  const user = localStorage.getItem('authUser');
  return user ? JSON.parse(user) : null;
}

/**
 * Check if user is logged in
 */
export function isLoggedIn() {
  return !!getAuthToken();
}

/**
 * Logout user
 */
export function logout() {
  localStorage.removeItem('authToken');
  localStorage.removeItem('authUser');
  // Stop push notifications for this user on this device
  unregisterCurrentDevice();
}

/**
 * Get authorization header for API calls
 */
export function getAuthHeader() {
  const token = getAuthToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

/**
 * Authenticated fetch - handles token errors and returns parsed JSON
 * Automatically logs out and redirects on invalid/expired tokens
 */
async function authFetch(url, options = {}) {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Not logged in');
  }
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers
    }
  });

  // Handle auth errors - auto-logout on stale tokens
  if (response.status === 401 || response.status === 403) {
    const data = await response.json().catch(() => ({}));
    const errorMsg = data.error || 'Authentication failed';
    
    if (errorMsg.includes('token') || errorMsg.includes('expired') || errorMsg.includes('Access token required')) {
      logout();
      window.location.href = '/';
      throw new Error('Session expired. Please log in again.');
    }
    
    throw new Error(errorMsg);
  }

  // Handle other errors
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Request failed');
  }

  // Return parsed JSON directly
  return await response.json();
}

/**
 * Fetch current user profile
 */
export async function fetchCurrentUser() {
  try {
    const user = await authFetch(`${API_BASE_URL}/auth/me`);
    localStorage.setItem('authUser', JSON.stringify(user));
    return user;
  } catch (error) {
    logout();
    throw error;
  }
}

/**
 * Create new user (Admin only)
 */
export async function createUser(userData) {
  return authFetch(`${API_BASE_URL}/auth/users`, {
    method: 'POST',
    body: JSON.stringify(userData)
  });
}

/**
 * Get all users (Admin only)
 */
export async function fetchAllUsers() {
  return authFetch(`${API_BASE_URL}/auth/users`);
}

/**
 * Update user (Admin only)
 */
export async function updateUser(userId, userData) {
  return authFetch(`${API_BASE_URL}/auth/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(userData)
  });
}

/**
 * Reset user password (Admin only)
 */
export async function resetUserPassword(userId, newPassword) {
  return authFetch(`${API_BASE_URL}/auth/users/${userId}/reset-password`, {
    method: 'POST',
    body: JSON.stringify({ newPassword })
  });
}

/**
 * Delete user (Admin only)
 */
export async function deleteUser(userId) {
  return authFetch(`${API_BASE_URL}/auth/users/${userId}`, {
    method: 'DELETE'
  });
}

/**
 * Bulk import users (Admin only)
 */
export async function bulkImportUsers(users) {
  return authFetch(`${API_BASE_URL}/auth/users/bulk-import`, {
    method: 'POST',
    body: JSON.stringify({ users })
  });
}
